import React from "react";

/**
 * Sprint 4X — Badge stato veicolo a 2 assi (disponibilita_fisica + stato_commerciale).
 *
 * Logica visualizzazione:
 *  - AVAILABLE + nessuna vendita        → "Disponibile" (singolo, verde)
 *  - VIRTUAL/INCOMING + nessuna vendita → "Virtuale" / "In arrivo" (singolo)
 *  - AVAILABLE + impegnata              → "Impegnata" (singolo)
 *  - AVAILABLE + venduta                → "Venduta" (singolo)
 *  - AVAILABLE + consegnata             → "Consegnata" (singolo)
 *  - VIRTUAL/INCOMING + impegnata       → DOPPIO badge
 *  - VIRTUAL/INCOMING + venduta         → DOPPIO badge
 *  - * + draft o annullata o null       → solo disponibilità fisica
 *
 * Props:
 *   - disponibilita_fisica: VIRTUAL | INCOMING | AVAILABLE
 *   - stato_commerciale:    null | draft | impegnata | venduta | consegnata | annullata
 *   - size: "sm" | "md"
 */

const FISICA_LABELS = {
  VIRTUAL: "Virtuale",
  INCOMING: "In arrivo",
  AVAILABLE: "Disponibile",
};
const FISICA_COLOR = {
  VIRTUAL: "bg-slate-100 text-slate-700 border-slate-200",
  INCOMING: "bg-sky-100 text-sky-800 border-sky-200",
  AVAILABLE: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const COMM_LABELS = {
  draft: "Bozza",
  impegnata: "Impegnata",
  venduta: "Venduta",
  consegnata: "Consegnata",
  annullata: "Annullata",
};
const COMM_COLOR = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  impegnata: "bg-amber-100 text-amber-800 border-amber-200",
  venduta: "bg-red-100 text-red-800 border-red-200",
  consegnata: "bg-violet-100 text-violet-800 border-violet-200",
  annullata: "bg-slate-200 text-slate-600 border-slate-300",
};

function Badge({ label, color, testid, size }) {
  const px = size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs";
  return (
    <span
      className={`inline-flex items-center font-medium rounded-full border ${color} ${px}`}
      data-testid={testid}
    >
      {label}
    </span>
  );
}

export default function VehicleStatusBadge({
  disponibilita_fisica,
  stato_commerciale,
  size = "md",
}) {
  // Sprint 4Z.2 B4+B5+E2: modalità "solo stato commerciale" (no disp_fisica props)
  // → renderizza singolo badge dello stato commerciale, colorato.
  if (stato_commerciale && !disponibilita_fisica) {
    if (stato_commerciale === "annullata") {
      return (
        <Badge
          label="Annullata"
          color="bg-rose-100 text-rose-700 border-rose-200"
          testid="vsb-comm-annullata-only"
          size={size}
        />
      );
    }
    return (
      <Badge
        label={COMM_LABELS[stato_commerciale] || stato_commerciale}
        color={COMM_COLOR[stato_commerciale] || "bg-slate-100 text-slate-600 border-slate-200"}
        testid={`vsb-comm-${stato_commerciale}-only`}
        size={size}
      />
    );
  }

  const df = disponibilita_fisica || "VIRTUAL";
  const sc = stato_commerciale; // può essere null

  // Stati commerciali "attivi" che dominano la visualizzazione singola in caso AVAILABLE
  const commActive = ["impegnata", "venduta", "consegnata"];

  // Sprint 4Z.1 F1: per status="annullata" mostriamo SEMPRE il badge commerciale
  // (rosso/slate) per dare riconoscibilità all'archivio annullate.
  if (sc === "annullata") {
    return (
      <span className="inline-flex items-center gap-1.5" data-testid="vsb-annullata-wrap">
        <Badge
          label={FISICA_LABELS[df] || df}
          color={FISICA_COLOR[df] || "bg-slate-100 text-slate-700 border-slate-200"}
          testid={`vsb-fisica-${df}`}
          size={size}
        />
        <Badge
          label="Annullata"
          color="bg-rose-100 text-rose-700 border-rose-200"
          testid="vsb-comm-annullata"
          size={size}
        />
      </span>
    );
  }

  // CASO: nessuna vendita attiva (null, draft)
  if (!sc || !commActive.includes(sc)) {
    return (
      <Badge
        label={FISICA_LABELS[df] || df}
        color={FISICA_COLOR[df] || "bg-slate-100 text-slate-700 border-slate-200"}
        testid={`vsb-fisica-${df}`}
        size={size}
      />
    );
  }

  // CASO: AVAILABLE + commerciale attivo → solo badge commerciale
  if (df === "AVAILABLE") {
    return (
      <Badge
        label={COMM_LABELS[sc]}
        color={COMM_COLOR[sc]}
        testid={`vsb-comm-${sc}`}
        size={size}
      />
    );
  }

  // CASO: VIRTUAL/INCOMING + impegnata/venduta → DOPPIO badge
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="vsb-double">
      <Badge
        label={FISICA_LABELS[df] || df}
        color={FISICA_COLOR[df] || "bg-slate-100 text-slate-700 border-slate-200"}
        testid={`vsb-fisica-${df}`}
        size={size}
      />
      <Badge
        label={COMM_LABELS[sc]}
        color={COMM_COLOR[sc]}
        testid={`vsb-comm-${sc}`}
        size={size}
      />
    </span>
  );
}
